import { isDefined } from '@e-socna/utils';
import type {
  RetryStrategyFn,
  RetryContext,
  RetryDecision,
} from '@e-socna/better-fetch';

import {
  handleNetworkError,
  calculateMinDelayForRate,
  handleServerError,
} from '../../utils/strategy.util';
import {
  MAX_RETRY_DELAY_MS,
  DEFAULT_429_DELAY_MS,
} from '../../proxy/constants/delay.constant';

import { PISTE_RATE_LIMITS } from '../constants/rate-limits.constant';

/**
 * Create a retry strategy for PISTE API
 *
 * @param serviceType - Type of PISTE service (default: 2 msg/s, bulk: 20 msg/s)
 * @returns Retry strategy function configured for the service rate limit
 *
 * @example
 * ```typescript
 * import { pisteRetryStrategy } from '@e-socna/gis';
 *
 * // For standard endpoints (2 msg/s)
 * const data = await betterFetch(url, {}, {
 *   retryStrategy: pisteRetryStrategy('default'),
 * });
 *
 * // For bulk endpoints (20 msg/s)
 * const bulkData = await betterFetch(url, {}, {
 *   retryStrategy: pisteRetryStrategy('bulk'),
 * });
 * ```
 */

export const pisteRetryStrategy =
  (serviceType: keyof typeof PISTE_RATE_LIMITS = 'default'): RetryStrategyFn =>
  ({ statusCode, attempt, retryAfterMs }: RetryContext): RetryDecision => {
    // Network errors and timeouts
    if (typeof statusCode === 'undefined') {
      return handleNetworkError(attempt);
    }

    // 429 Rate Limit
    if (statusCode === 429) {
      if (isDefined(retryAfterMs)) {
        return {
          shouldRetry: true,
          delayMs: Math.min(retryAfterMs, MAX_RETRY_DELAY_MS),
        };
      }

      // PISTE has strict rate limits, use longer delays
      const rateLimit = PISTE_RATE_LIMITS[serviceType];
      const minDelay = calculateMinDelayForRate(rateLimit);
      const backoffDelay =
        Math.max(minDelay, DEFAULT_429_DELAY_MS) * 2 ** attempt;

      return {
        shouldRetry: true,
        delayMs: Math.min(backoffDelay, MAX_RETRY_DELAY_MS),
      };
    }

    // 5xx Server errors
    if (statusCode >= 500 && statusCode < 600) {
      return handleServerError(attempt);
    }

    return { shouldRetry: false, delayMs: 0 };
  };
